/* eslint camelcase: "off" */
/* TODO: created_by_name is sent as camelcase from the server */
import React, { useCallback } from "react";
import dayjs from "dayjs";
import { Accordion, Button, ColorSwatch } from "@mantine/core";
import AuditTimestamps from "./AuditTimestamps";
import {
  IBlockedDateEvent,
  IGroupedBlockedDateEvents,
} from "../../@types/blockedDates";

interface IBlockDatesOverViewProps {
  groupedBlockedDates: IGroupedBlockedDateEvents;
  setOpenedEditBlockedDateDrawer: (value: boolean) => void;
  updateSelectedBlockDate: (uuid: string) => void;
  setActiveBlockDateTab: (
    tab: "overview" | "calendar" | "edit" | "history"
  ) => void;
}

interface SingleBlockedDateProps extends IBlockedDateEvent {
  type: "past" | "current" | "future";
  setOpenedEditBlockedDateDrawer: (value: boolean) => void;
  updateSelectedBlockDate: (uuid: string) => void;
  setActiveBlockDateTab: (
    tab: "overview" | "calendar" | "edit" | "history"
  ) => void;
}

const SingleBlockedDate: React.FC<SingleBlockedDateProps> = ({
  type,
  setOpenedEditBlockedDateDrawer,
  updateSelectedBlockDate,
  setActiveBlockDateTab,
  id,
  title,
  employees,
  start,
  end,
  created_by_name,
  created_at,
  updated_at,
  updated_by_name,
}) => {
  const handleEdit = useCallback(() => {
    updateSelectedBlockDate(id);
    setOpenedEditBlockedDateDrawer(true);
  }, [id, setOpenedEditBlockedDateDrawer, updateSelectedBlockDate]);

  const handleManageEmployees = useCallback(() => {
    updateSelectedBlockDate(id);
    setActiveBlockDateTab("edit");
  }, [id, setActiveBlockDateTab, updateSelectedBlockDate]);

  const getColor = useCallback(() => {
    switch (type) {
      case "past":
        return "#9ca6ae";
      case "current":
        return "#ED7687";
      default:
        return "rgba(6, 33, 51, 1)";
    }
  }, [type]);

  return (
    <div className="flex items-center gap-3 py-4 [&:not(:last-child)]:border-b">
      <ColorSwatch color={getColor()} size={30} className="rounded-full" />
      <div className="flex flex-col gap-0 grow">
        <div className="font-bold text-[16px] pb-[5px]">
          {title} -{" "}
          {employees
            ? `Individual Block (${employees.length})`
            : "Company Block"}
        </div>
        <div className="text-xs text-gray-500">
          {dayjs(start).format("DD MMM")} - {dayjs(end).format("DD MMM YYYY")}
        </div>
        <AuditTimestamps
          created_at={created_at}
          created_by_name={created_by_name}
          updated_at={updated_at}
          updated_by_name={updated_by_name}
        />
      </div>
      <div className="flex gap-3">
        {employees && (
          <Button variant="outline" size="xs" onClick={handleManageEmployees}>
            Manage Employees
          </Button>
        )}
        <Button variant="outline" size="xs" onClick={handleEdit}>
          Edit
        </Button>
        {!employees && (
          <Button
            variant="outline"
            size="xs"
            onClick={() => setActiveBlockDateTab("history")}
          >
            History Log
          </Button>
        )}
      </div>
    </div>
  );
};

const BlockedDatesOverView: React.FC<IBlockDatesOverViewProps> = ({
  groupedBlockedDates,
  ...rest
}) => (
  <div>
    <Accordion
      iconPosition="right"
      classNames={{
        label: "text-lg font-bold",
        contentInner: "px-0 py-0",
        control: "px-0",
        item: "border-none border-0",
      }}
      initialItem={0}
      multiple
    >
      {groupedBlockedDates.currentEvents.length > 0 && (
        <Accordion.Item label="Today's active blocked dates">
          {groupedBlockedDates.currentEvents.map((event) => (
            <SingleBlockedDate
              {...event}
              {...rest}
              type="current"
              key={`${event.id}current`}
            />
          ))}
        </Accordion.Item>
      )}
      {groupedBlockedDates.futureEvents.length > 0 && (
        <Accordion.Item label="Future blocked dates">
          {groupedBlockedDates.futureEvents.map((event) => (
            <SingleBlockedDate
              {...event}
              {...rest}
              type="future"
              key={`${event.id}future`}
            />
          ))}
        </Accordion.Item>
      )}
      {groupedBlockedDates.pastEvents.length > 0 && (
        <Accordion.Item label="Past blocked dates (last 30 days only)">
          {groupedBlockedDates.pastEvents.map((event) => (
            <SingleBlockedDate
              {...event}
              {...rest}
              type="past"
              key={`${event.id}past`}
            />
          ))}
        </Accordion.Item>
      )}
    </Accordion>
  </div>
);

export default BlockedDatesOverView;
